import { router } from '@inertiajs/react';
import React, { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { Facets, FilterConfig } from '@/types';

type AttrValue = string[] | [number, number] | boolean;

interface Filters {
    q: string;
    sort: string;
    location: { city: string; region: string; neighborhoodId: string };
    services: string[];
    priceRange: [number, number];
    availability: { onlineNow: boolean; verified: boolean; newProfile: boolean };
    attrs: Record<string, AttrValue>;
}

interface FiltersContextType {
    filters: Filters;
    setFilters: React.Dispatch<React.SetStateAction<Filters>>;
    resetFilters: () => void;
    applyFilters: () => void;
    filterConfig: FilterConfig;
    facets: Facets;
}

const defaultFilters: Filters = {
    q: '',
    sort: '',
    location: { city: '', region: '', neighborhoodId: '' },
    services: [],
    priceRange: [0, 1000],
    availability: { onlineNow: false, verified: false, newProfile: false },
    attrs: {},
};

const emptyFilterConfig: FilterConfig = { services: [], attributes: [] };
const emptyFacets: Facets = { attrs: {}, services: {} };

const FiltersContext = createContext<FiltersContextType | undefined>(undefined);

const toRequestParams = (f: Filters): Record<string, unknown> => {
    const params: Record<string, unknown> = {};

    if (f.q) params.q = f.q;
    if (f.sort && f.sort !== 'top') params.sort = f.sort;
    if (f.location.city) params.city = f.location.city;
    if (f.location.region) params.region = f.location.region;
    if (f.location.neighborhoodId) params.neighborhood_id = f.location.neighborhoodId;
    if (f.services.length) params.services = f.services;
    if (f.priceRange[0] > 0) params.price_min = f.priceRange[0];
    if (f.priceRange[1] < 1000) params.price_max = f.priceRange[1];
    if (f.availability.verified) params.verified = '1';
    if (f.availability.newProfile) params.new = '1';

    const attrs: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(f.attrs)) {
        if (typeof value === 'boolean') {
            if (value) attrs[key] = '1';
        } else if (Array.isArray(value) && value.length > 0) {
            attrs[key] = value;
        }
    }
    if (Object.keys(attrs).length) params.attrs = attrs;

    return params;
};

interface FiltersProviderProps {
    children: ReactNode;
    initialFilters?: Partial<Filters>;
    filterConfig?: FilterConfig;
    facets?: Facets;
}

export const FiltersProvider: React.FC<FiltersProviderProps> = ({
    children,
    initialFilters,
    filterConfig = emptyFilterConfig,
    facets = emptyFacets,
}) => {
    const [filters, setFilters] = useState<Filters>({
        ...defaultFilters,
        ...initialFilters,
    });

    const isFirstRender = useRef(true);

    const applyFilters = () => {
        router.visit(window.location.pathname, {
            data: toRequestParams(filters) as Record<string, string | number | boolean | undefined>,
            preserveState: true,
            replace: true,
        });
    };

    const resetFilters = () => setFilters(defaultFilters);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        const timer = setTimeout(() => {
            router.visit(window.location.pathname, {
                data: toRequestParams(filters) as Record<string, string | number | boolean | undefined>,
                preserveState: true,
                replace: true,
            });
        }, 500);

        return () => clearTimeout(timer);
    }, [filters]);

    return (
        <FiltersContext.Provider value={{ filters, setFilters, resetFilters, applyFilters, filterConfig, facets }}>
            {children}
        </FiltersContext.Provider>
    );
};

export const useFilters = () => {
    const context = useContext(FiltersContext);
    if (!context) {
        throw new Error('useFilters must be used within a FiltersProvider');
    }
    return context;
};
